import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getAttributionProps } from '@/lib/attribution';

type CtaButtonProps = {
  /** PostHog `location` property, e.g. 'hero' */
  location: string;
  children: ReactNode;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  /**
   * ink = primary pill, clay = high-emphasis action, paper = inverted (dark
   * bands), quiet = low-emphasis outline pill for inline/secondary placements.
   * `quiet` is flat-only — there is no raised deck for a bordered face.
   */
  variant?: 'ink' | 'clay' | 'paper' | 'quiet';
  /**
   * 'flat' is the standard shadcn pill. '3d' is the evilbuttons 3d-button
   * treatment — a face sitting on a darker deck that it presses into — used by
   * the marketing page's in-body CTAs. The masthead stays flat, where a raised
   * pill reads as too heavy next to a 40px nav row.
   */
  depth?: 'flat' | '3d';
};

// --- 3D treatment -----------------------------------------------------------
// Face and deck always share one token: the deck is that same colour stepped
// with a filter, so the shadow block is a real shade of the face in whichever
// theme is active rather than a second hardcoded value.
const DEPTH_3D_SKINS = {
  clay: { face: 'bg-ring text-background', deck: 'bg-ring brightness-75' },
  ink: { face: 'bg-primary text-primary-foreground', deck: 'bg-primary brightness-75' },
  paper: {
    face: 'bg-primary-foreground text-primary',
    // This variant inverts with the theme, so the deck has to step the other
    // way in dark or it disappears into a near-black face on a light band.
    deck: 'bg-primary-foreground brightness-75 dark:brightness-[1.6]',
  },
} as const;

// Flat treatment maps each skin onto a brand button variant — call sites never
// name a shadcn variant directly (see apps/landing/CLAUDE.md → Brand Design).
const FLAT_VARIANTS = {
  ink: 'default',
  clay: 'action',
  paper: 'default',
  quiet: 'outline',
} as const;

// 'lg' shares the default footprint — with the deck accounted for, the 3D face
// already stands taller than a flat lg pill.
const DEPTH_3D_SIZES = {
  default: { face: 'px-6 py-2.5 text-sm sm:px-8 sm:py-3 sm:text-base', wrapper: 'min-w-38' },
  lg: { face: 'px-6 py-2.5 text-sm sm:px-8 sm:py-3 sm:text-base', wrapper: 'min-w-38' },
  sm: { face: 'px-5 py-2 text-sm', wrapper: 'min-w-28' },
} as const;

const pressTransition = {
  type: 'spring',
  stiffness: 620,
  damping: 32,
  mass: 0.5,
} as const;

// The deck barely moves, which is what sells the face travelling down into it.
const deckVariants: Variants = {
  rest: { y: 0, scaleX: 1 },
  hover: { y: 1, scaleX: 0.995 },
  tap: { y: -1, scaleX: 0.985 },
};

const faceVariants: Variants = {
  rest: { y: 0, rotateX: 0, scale: 1 },
  hover: { y: -2, rotateX: 0, scale: 1.005 },
  tap: { y: 6, rotateX: -6, scale: 0.99 },
};

const pressSpotVariants: Variants = {
  rest: { opacity: 0, scaleX: 0.25 },
  hover: { opacity: 0.3, scaleX: 0.45 },
  tap: { opacity: 1, scaleX: 1 },
};

const labelVariants: Variants = {
  rest: { y: 0 },
  hover: { y: -1 },
  tap: { y: 1 },
};

/**
 * The page's only CTA. Always renders a router <Link> to /login — these are
 * navigation CTAs, so they stay anchors for middle-click, SEO and assistive
 * tech — and always fires the same `signup_cta_clicked` event with attribution.
 * `depth` picks the visual treatment; the wiring is shared.
 */
export function CtaButton({
  location,
  children,
  className,
  size = 'lg',
  variant = 'ink',
  depth = 'flat',
}: CtaButtonProps) {
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();

  const handleClick = () =>
    posthog.capture('signup_cta_clicked', { location, ...getAttributionProps() });

  if (depth === '3d' && variant !== 'quiet') {
    const skin = DEPTH_3D_SKINS[variant];
    const footprint = DEPTH_3D_SIZES[size];

    return (
      <Link
        to="/login"
        search={{ state: undefined }}
        onClick={handleClick}
        className={cn(
          'group inline-flex rounded-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          className,
        )}
      >
        <motion.span
          initial="rest"
          animate="rest"
          whileHover={reduceMotion ? undefined : 'hover'}
          whileTap={reduceMotion ? undefined : 'tap'}
          className={cn(
            'relative isolate inline-flex w-full pb-1.5 [perspective:600px]',
            footprint.wrapper,
          )}
        >
          <motion.span
            aria-hidden="true"
            variants={deckVariants}
            transition={pressTransition}
            className={cn(
              'absolute inset-x-0 bottom-0 top-1.5 -z-10 rounded-full shadow-lg',
              skin.deck,
            )}
          />

          <motion.span
            variants={faceVariants}
            transition={pressTransition}
            className={cn(
              'relative z-10 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-full font-semibold [transform-style:preserve-3d]',
              skin.face,
              footprint.face,
            )}
          >
            {/* Darkens under the thumb on press. `bg-current` picks up the face's
                own label colour, so it contrasts on every variant and theme
                without a per-variant override. */}
            <motion.span
              aria-hidden="true"
              variants={pressSpotVariants}
              transition={pressTransition}
              className="pointer-events-none absolute inset-y-1 left-1/2 w-10 -translate-x-1/2 rounded-full bg-current/20 blur-sm"
            />
            {/* The source block's sweeping gloss stripe is deliberately dropped:
                its inset-y-1 left a band of un-glossed pill top and bottom, and
                on a rounded-full face no inset reads cleanly. */}
            <motion.span
              variants={labelVariants}
              transition={pressTransition}
              className="relative z-10 inline-flex items-center gap-2"
            >
              {children}
            </motion.span>
          </motion.span>
        </motion.span>
      </Link>
    );
  }

  return (
    <Button
      asChild
      size={size}
      variant={FLAT_VARIANTS[variant]}
      onClick={handleClick}
      className={cn(
        variant === 'paper' && 'bg-background text-foreground hover:bg-background/90',
        className,
      )}
    >
      <Link to="/login" search={{ state: undefined }}>
        {children}
      </Link>
    </Button>
  );
}
